import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";

import { Paragraph } from "@fronterahealth/frontera-ui-components";

import { CreateUpdateReportGoalMutationVariables } from "@api/graphql/types-and-hooks";
import { useSuggestionCount } from "@components/GoalsSections/ReviewGoalHelper";
import { GOAL_FIELD_IDS, isEmpty } from "@components/GoalsSections/helper";
import { SuggestionIndicator } from "@components/QaReview/components/SuggestionIndicator";
import { useFieldReview } from "@components/QaReview/hooks/useFieldReview";
import { useInlineTreatmentPlanEdit } from "@components/TreatmentPlanFieldForm/useInlineTreatmentPlanEdit";
import { FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";

interface GASFormData {
  baselineData: string;
  gasLevel1: string;
  masteryCriteria: string;
  gasLevel3: string;
  gasLevel4: string;
}

interface GasPanelProps {
  GASData: GASFormData;
  onSubmitCallback: () => void;
  readOnly?: boolean;
  goalId?: string;
  learnerId: string;
  goalName: string;
  reportId: string;
  domainId: string | null;
  isSidePanel?: boolean;
}

interface GasLevelItemProps {
  level: number;
  label: string;
  description: string;
}

const GasLevelItem: React.FC<GasLevelItemProps> = ({ level, label, description }) => (
  <Paragraph displayType="normal" colorType="primary">
    <li className="text-sm text-text-primary">
      <span className="font-semibold">
        {level} {label}
      </span>{" "}
      = {description}
    </li>
  </Paragraph>
);

const GasLevelSidePanelItem: React.FC<GasLevelItemProps> = ({ level, label, description }) => (
  <div className="flex flex-col">
    <Paragraph size="small" colorType="secondary" displayType="normal">
      {level} - {label}
    </Paragraph>
    <Paragraph displayType="normal" colorType="primary">
      {description || "--"}
    </Paragraph>
  </div>
);

export const GasPanel: React.FC<GasPanelProps> = ({
  GASData,
  readOnly = false,
  goalId,
  learnerId,
  reportId,
  onSubmitCallback,
  domainId,
  isSidePanel = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const getSuggestionCount = useSuggestionCount(goalId || "", "stg");
  const { activate } = useFieldReview();

  const { saveGoalFields } = useInlineTreatmentPlanEdit({
    goalId,
    reportId,
    domainId,
    learnerId,
    onSuccess: onSubmitCallback,
  });

  const { formState, onSubmit, RegisteredFormTextArea, reset } = useFormUtils<CreateUpdateReportGoalMutationVariables>({
    mutationFn: async (params) => {
      const result = await saveGoalFields({
        baselineData: params.goalInput.baselineData ?? "",
        gasLevel1: params.goalInput.gasLevel1 ?? "",
        masteryCriteria: params.goalInput.masteryCriteria ?? "",
        gasLevel3: params.goalInput.gasLevel3 ?? "",
        gasLevel4: params.goalInput.gasLevel4 ?? "",
      });
      if (!result.success) {
        throw new Error(result.error ?? "Failed to save GAS levels");
      }
      setIsEditing(false);
      setIsHovered(false);
    },
  });

  // Sync form with GASData only when entering edit mode, so we show the latest saved values
  // (e.g. from getReassessmentReportGoals after cache update) and avoid overwriting the form
  // with stale props on every GASData change while editing.
  const prevIsEditingRef = useRef(false);
  useEffect(() => {
    if (goalId && isEditing && !prevIsEditingRef.current && GASData) {
      reset({
        goalInput: {
          baselineData: GASData.baselineData || "",
          gasLevel1: GASData.gasLevel1 || "",
          masteryCriteria: GASData.masteryCriteria || "",
          gasLevel3: GASData.gasLevel3 || "",
          gasLevel4: GASData.gasLevel4 || "",
        },
      });
    }
    prevIsEditingRef.current = isEditing;
  }, [goalId, isEditing, GASData, reset]);
  const gasLevels = [
    {
      level: 0,
      label: "Baseline Data",
      description: GASData.baselineData,
      fieldKey: "baselineData" as const,
      helperText: "Imports from baseline data",
      fieldType: GOAL_FIELD_IDS.BASELINE_DATA,
    },
    {
      level: 1,
      label: "Progress but Not at Target",
      description: GASData.gasLevel1,
      fieldKey: "gasLevel1" as const,
      fieldType: GOAL_FIELD_IDS.GAS_LEVEL_1,
    },
    {
      level: 2,
      label: "Target Treatment Goal",
      description: GASData.masteryCriteria,
      fieldKey: "masteryCriteria" as const,
      helperText: "Imports from mastery criteria",
      fieldType: GOAL_FIELD_IDS.MASTERY_CRITERIA,
    },
    {
      level: 3,
      label: "More than Expected",
      description: GASData.gasLevel3,
      fieldKey: "gasLevel3" as const,
      fieldType: GOAL_FIELD_IDS.GAS_LEVEL_3,
    },
    {
      level: 4,
      label: "Much More than Expected",
      description: GASData.gasLevel4,
      fieldKey: "gasLevel4" as const,
      fieldType: GOAL_FIELD_IDS.GAS_LEVEL_4,
    },
  ];

  // Handle click outside to submit and close
  useEffect(() => {
    const handleClickOutside = async (event: MouseEvent) => {
      if (isEditing && containerRef.current && !containerRef.current.contains(event.target as Node)) {
        try {
          await onSubmit();
        } catch {
          // errors are already surfaced by useFormUtils notifyError
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing, onSubmit]);

  const handlePanelClick = () => {
    if (!readOnly && !isEditing) {
      setIsEditing(true);
    }
  };

  const baselineDataSuggestionCount = getSuggestionCount(GOAL_FIELD_IDS.BASELINE_DATA);
  const gasLevel1SuggestionCount = getSuggestionCount(GOAL_FIELD_IDS.GAS_LEVEL_1);
  const masteryCriteriaSuggestionCount = getSuggestionCount(GOAL_FIELD_IDS.MASTERY_CRITERIA);
  const gasLevel3SuggestionCount = getSuggestionCount(GOAL_FIELD_IDS.GAS_LEVEL_3);
  const gasLevel4SuggestionCount = getSuggestionCount(GOAL_FIELD_IDS.GAS_LEVEL_4);

  return (
    <div
      ref={containerRef}
      onClick={!isEditing ? handlePanelClick : undefined}
      onMouseEnter={() => !readOnly && !isEditing && setIsHovered(true)}
      onMouseLeave={() => !isEditing && setIsHovered(false)}
      className={clsx(
        "rounded-xl bg-white transition-all duration-200 ease-in-out",
        (isEditing || (!readOnly && isHovered)) && "p-4 border border-limestone-200",
        !readOnly && !isEditing && "cursor-pointer",
      )}
    >
      {isEditing ? (
        <FormContainer onSubmit={onSubmit}>
          <div className="space-y-4">
            <div className="relative">
              <RegisteredFormTextArea
                formKey="goalInput.baselineData"
                formState={formState}
                label="0. Baseline Data"
                rows={2}
                required={isSidePanel}
                formNoValidate={isSidePanel}
                hideHintText
                onFocus={() => activate(`${goalId}::${GOAL_FIELD_IDS.BASELINE_DATA}`)}
              />
              {baselineDataSuggestionCount > 0 && (
                <div className="absolute top-0 right-0">
                  <SuggestionIndicator
                    fieldId={`${goalId}::${GOAL_FIELD_IDS.BASELINE_DATA}`}
                    suggestionCount={baselineDataSuggestionCount}
                  />
                </div>
              )}
            </div>

            <div className="relative">
              <RegisteredFormTextArea
                formKey="goalInput.gasLevel1"
                formState={formState}
                label="1. Progress but Not at Target"
                required={false}
                rows={2}
                formNoValidate={isSidePanel}
                hideHintText
                onFocus={() => activate(`${goalId}::${GOAL_FIELD_IDS.GAS_LEVEL_1}`)}
              />
              {gasLevel1SuggestionCount > 0 && (
                <div className="absolute top-0 right-0">
                  <SuggestionIndicator
                    fieldId={`${goalId}::${GOAL_FIELD_IDS.GAS_LEVEL_1}`}
                    suggestionCount={gasLevel1SuggestionCount}
                  />
                </div>
              )}
            </div>

            <div className="relative">
              <RegisteredFormTextArea
                formKey="goalInput.masteryCriteria"
                formState={formState}
                label="2. Target Treatment Goal"
                rows={3}
                required={isSidePanel}
                formNoValidate={isSidePanel}
                hideHintText
                onFocus={() => activate(`${goalId}::${GOAL_FIELD_IDS.MASTERY_CRITERIA}`)}
              />
              {masteryCriteriaSuggestionCount > 0 && (
                <div className="absolute top-0 right-0">
                  <SuggestionIndicator
                    fieldId={`${goalId}::${GOAL_FIELD_IDS.MASTERY_CRITERIA}`}
                    suggestionCount={masteryCriteriaSuggestionCount}
                  />
                </div>
              )}
            </div>

            <div className="relative">
              <RegisteredFormTextArea
                formKey="goalInput.gasLevel3"
                formState={formState}
                label="3. More than Expected"
                required={false}
                rows={2}
                formNoValidate={isSidePanel}
                hideHintText
                onFocus={() => activate(`${goalId}::${GOAL_FIELD_IDS.GAS_LEVEL_3}`)}
              />
              {gasLevel3SuggestionCount > 0 && (
                <div className="absolute top-0 right-0">
                  <SuggestionIndicator
                    fieldId={`${goalId}::${GOAL_FIELD_IDS.GAS_LEVEL_3}`}
                    suggestionCount={gasLevel3SuggestionCount}
                  />
                </div>
              )}
            </div>

            <div className="relative">
              <RegisteredFormTextArea
                formKey="goalInput.gasLevel4"
                formState={formState}
                label="4. Much More than Expected"
                required={false}
                rows={2}
                formNoValidate={isSidePanel}
                hideHintText
                onFocus={() => activate(`${goalId}::${GOAL_FIELD_IDS.GAS_LEVEL_4}`)}
              />
              {gasLevel4SuggestionCount > 0 && (
                <div className="absolute top-0 right-0">
                  <SuggestionIndicator
                    fieldId={`${goalId}::${GOAL_FIELD_IDS.GAS_LEVEL_4}`}
                    suggestionCount={gasLevel4SuggestionCount}
                  />
                </div>
              )}
            </div>
          </div>
        </FormContainer>
      ) : isSidePanel ? (
        <div className="flex flex-col gap-y-3">
          {gasLevels.map((item) => (
            <GasLevelSidePanelItem
              key={item.level}
              level={item.level}
              label={item.label}
              description={isEmpty(item.description) ? "--" : item.description}
            />
          ))}
        </div>
      ) : (
        <ul className="list-disc list-inside space-y-2">
          {gasLevels.map((item) => {
            const count = getSuggestionCount(item.fieldType);
            const fieldId = `${goalId}::${item.fieldType}`;

            return (
              <div className="flex flex-row items-center justify-between gap-x-2" key={item.level}>
                <GasLevelItem
                  key={item.level}
                  level={item.level}
                  label={item.label}
                  description={isEmpty(item.description) ? "--" : item.description}
                />
                {count > 0 && <SuggestionIndicator fieldId={fieldId} suggestionCount={count} />}
              </div>
            );
          })}
        </ul>
      )}
    </div>
  );
};
